import { isEmptyOrNull } from "@ifx-react/ifx-react-core";
import { useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { LoadCarrierProperties } from "../../store/actions/common/actionCreators";

const useCarrierId = () => {
  return useSelector(state => {
    let val = state.PortalReducer.UserContextReducer.selectedAirline;
    if (val) {
      return val.id;
    }
    return null;
  });
};

export const useCarrierProperties = (
  propertyNames,
  booleanPropertyNames = []
) => {
  const dispatch = useDispatch();
  const carrierId = useCarrierId();
  const carrierProperties = useSelector(
    state => state.CommonReducer.CarrierPropertiesReducer
  );

  const _propertyNames = [...propertyNames, ...booleanPropertyNames];

  const requestPropertyKeys = useMemo(() => {
    if (!isEmptyOrNull(carrierProperties)) {
      const keys = Object.keys(carrierProperties);
      return _propertyNames.filter(propName => !keys.includes(propName));
    }
    return [];
  }, [carrierProperties, JSON.stringify(_propertyNames)]);

  useEffect(() => {
    //console.log("useCarrierProperties useEffect 1");
    if (requestPropertyKeys.length > 0 && carrierId != null) {
      //console.log("useCarrierProperties useEffect 2");
      //console.log("useCarrierProperties dispatch", requestPropertyKeys);
      dispatch(
        LoadCarrierProperties.REQUEST({
          propertyNames: requestPropertyKeys,
          carrierId,
        })
      );
    }
  }, [carrierId, JSON.stringify(requestPropertyKeys)]);

  return useMemo(() => {
    //console.log("useCarrierProperties result memo", propertyNames);
    return _propertyNames.reduce((acc, key) => {
      let val = carrierProperties[key]?.value || null;
      if (booleanPropertyNames.includes(key)) {
        val = val === "1" || val?.toLowerCase() === "true" || false;
      }
      return ((acc[key] = val) && acc) || acc;
    }, {});
  }, [carrierId, carrierProperties, JSON.stringify(_propertyNames)]);
};

export const useCarrierProperty = (
  propertyName,
  { type = "string", split = ",", defaultValue = null } = {}
) => {
  const dispatch = useDispatch();
  const propertyObj = useSelector(
    state => state.CommonReducer.CarrierPropertiesReducer[propertyName]
  );

  const propertyVal = propertyObj?.value || null;

  const carrierId = useCarrierId();

  useEffect(() => {
    if (propertyObj == null && carrierId != null) {
      dispatch(
        LoadCarrierProperties.REQUEST({
          propertyNames: [propertyName],
          carrierId,
        })
      );
    }
  }, [propertyObj, carrierId]);

  return useMemo(() => {
    if (propertyVal != null) {
      if (type === "boolean") {
        return propertyVal === "1" || propertyVal.toLowerCase() === "true";
      } else if (type === "array") {
        return propertyVal.split(split);
      } else if (type === "number") {
        return Number(propertyVal);
      } else {
        return propertyVal;
      }
    } else {
      return defaultValue;
    }
  }, [propertyVal]);
};

export const useCarrierPropertyBool = (propertyName, options) =>
  useCarrierProperty(propertyName, { type: "boolean", ...options });

export const useIsCarrierPropertiesLoaded = propertyNames => {
  const carrierId = useCarrierId();
  const carrierProperties = useSelector(
    state => state.CommonReducer.CarrierPropertiesReducer
  );

  return useMemo(() => {
    return propertyNames.every(key => {
      return carrierProperties[key]?.status === 1;
    });
  }, [carrierId, carrierProperties, JSON.stringify(propertyNames)]);
};
